import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Tooltip from '@mui/material/Tooltip';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Modal,
    IconButton,
    TablePagination,
    TextField,
    Button
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import baseURL from '../../config';
import Navbar from '../../Navbar/Navbar';

const CrewAssigned = () => {
    const { crewingAgentId } = useParams();
    const [allCrews, setAllCrews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [modalDocs, setModalDocs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        const fetchAssignedCrew = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(
                    `${baseURL}/api/assigneCrew/byAgent/${crewingAgentId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const combined = res.data.data.flatMap((assignment) =>
                    assignment.crews.map((crewItem) => ({
                        ...crewItem,
                        crewData: crewItem.crewId || {},
                        CrewingAgentName: assignment.CrewingAgentName,
                        creatorName: assignment.creatorName,
                    }))
                );

                setAllCrews(combined);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to fetch assigned crew.');
            } finally {
                setLoading(false);
            }
        };

        fetchAssignedCrew();
    }, [crewingAgentId]);

    const handleOpenModal = (crew) => {
        const docs = Object.entries(crew)
            .filter(([_, doc]) => doc && doc.filePath)
            .map(([key, doc]) => {
                const label = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase());
                const url = `${baseURL}/${doc.filePath.replace(/\\/g, '/')}`;
                return {
                    label,
                    originalName: doc.originalName || label,
                    url
                };
            });

        setModalDocs(docs);
        setOpenModal(true);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredCrews = allCrews.filter((crewItem) => {
        const crew = crewItem.crewData;
        const name = (crew.name || crewItem.crewName || '').toLowerCase();
        const indos = (crew.indosno || '').toLowerCase();
        const rank = (crew.rankname || '').toLowerCase();
        const term = searchTerm.toLowerCase();

        return name.includes(term) || indos.includes(term) || rank.includes(term);
    });

    const paginatedCrews = filteredCrews.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <>
            <Navbar />
            <Box p={4}>
                <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search by Name, Indos, or Rank"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }}
                        sx={{ minWidth: 250 }}
                    />

                    <Typography variant="h6" sx={{ flexGrow: 1, textAlign: "center" }}>
                        All Assigned Crew Members
                    </Typography>

                    <Button
                        sx={{
                            backgroundColor: "red",
                            color: "white",
                            textTransform: "none",
                            ml: 2,
                        }}
                    >
                        Count: {allCrews?.length}
                    </Button>
                </Box>

                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#1976d2", color: "white" }}>
                                        <TableCell sx={{ color: "white" , width:"100px"}}><strong>Sr. No.</strong></TableCell>
                                        <TableCell sx={{ color: "white" }}><strong>Information</strong></TableCell>
                                        <TableCell sx={{ color: "white", width:"200px" }}><strong>Documents</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedCrews.map((crewItem, i) => {
                                        const crew = crewItem.crewData;
                                        return (
                                            <TableRow key={i}>
                                                <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                                                <TableCell>
                                                    <Typography><strong>Name:</strong> {crew.name || crewItem.crewName}</Typography>
                                                    <Typography><strong>Indos No:</strong> {crew.indosno || 'N/A'}</Typography>
                                                    <Typography><strong>Rank:</strong> {crew.rankname || 'N/A'}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="Crew Documents" arrow>
                                                        <IconButton onClick={() => handleOpenModal(crew)}>
                                                            <PictureAsPdfIcon sx={{ color: 'red' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>

                            <TablePagination
                                component="div"
                                count={filteredCrews.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                            />
                        </TableContainer>
                    </>
                )}
            </Box>

            {/* Modal for PDF Documents */}
            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: 600,
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: 2,
                        p: 3,
                        display: 'flex',
                        gap: 3,
                        flexDirection: 'column'
                    }}
                >
                    <Typography variant="h6" align="center" gutterBottom>
                        Crew Documents
                    </Typography>

                    {modalDocs.length === 0 ? (
                        <Typography align="center">No documents available.</Typography>
                    ) : (
                        <Box display="flex" flexDirection="column" gap={2}>
                            {modalDocs.map((doc, idx) => (
                                <Box
                                    key={idx}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    bgcolor="#fff"
                                    boxShadow={2}
                                    borderRadius={2}
                                    p={2}
                                >
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <PictureAsPdfIcon sx={{ color: 'red' }} />
                                        <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                                            {doc.label}
                                        </Typography>
                                    </Box>

                                    <a
                                        href={doc.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <Box
                                            sx={{
                                                bgcolor: '#1976d2',
                                                color: 'white',
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: 1,
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                '&:hover': {
                                                    bgcolor: '#1565c0',
                                                },
                                            }}
                                        >
                                            VIEW
                                        </Box>
                                    </a>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box mt={3} display="flex" justifyContent="center">
                        <button
                            onClick={() => setOpenModal(false)}
                            style={{
                                backgroundColor: '#d32f2f',
                                color: '#fff',
                                border: 'none',
                                padding: '8px 20px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                            }}
                        >
                            CLOSE
                        </button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default CrewAssigned;

